import { SupabaseClient } from '@supabase/supabase-js';

import { handleError } from '@common/utils/handleError';
import { SupabaseEventRecord } from '@interfaces';

export interface GetEventListParams {
	options: {
		dateFrom?: Date;
		dateTo?: Date;
		elsewherePresents?: boolean;
		excludeDice?: boolean;
		excludeIds?: number[];
		excludePast?: boolean;
		genres?: string[];
		highlight?: boolean;
		includeIds?: number[];
		justAnnounced?: boolean;
		range?: {
			from: number;
			to: number;
		};
		type?: 'club' | 'live';
		venues?: string[];
	};
	supabaseClient: SupabaseClient;
}

export async function getEventList({
	options,
	supabaseClient,
}: GetEventListParams) {
	try {
		let query = supabaseClient
			.from('events')
			.select<'*', SupabaseEventRecord>('*', { count: 'exact' })
			.eq('status', 'visible');

		// Include or exclude Elsewhere Presents events
		if (typeof options.elsewherePresents === 'boolean') {
			query = query.eq('elsewhere_presents', options.elsewherePresents);
		}

		// This is a temporary option to exclude Dice events as there are some duplicate events between Dice and Eventbrite.
		if (options.excludeDice === true) {
			query = query.neq('provider', 'dice');
		}

		if (options.excludeIds?.length) {
			query = query.not('id', 'in', `(${options.excludeIds.join(',')})`);
		}

		// Exclude past events if options.excludePast is true or undefined
		if (options.excludePast !== false) {
			query = query.gt('end_date', new Date().toISOString());
		}

		if (options.genres?.length) {
			// https://supabase.com/docs/reference/javascript/overlaps
			query = query.overlaps('genres', options.genres);
		}

		// Include or exclude highlight events
		if (typeof options.highlight === 'boolean') {
			query = query.eq('highlight', options.highlight);
		}

		if (options.includeIds?.length) {
			query = query.filter('id', 'in', `(${options.includeIds.join(',')})`);
		}

		if (options.justAnnounced === true) {
			// 14 days × 24 hours per day × 60 minutes per hour × 60 seconds per minute × 1000 milliseconds per second
			const twoWeeksBeforeNow = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);

			// Announced in the last two weeks
			query = query.gt('announcement_date', twoWeeksBeforeNow.toISOString());
		}

		if (options.type) {
			query = query.eq('type', options.type);
		}

		if (options.venues?.length) {
			query = query.overlaps('venues', options.venues);
		}

		if (options.dateFrom) {
			query = query.gte('start_date', options.dateFrom.toISOString());
		}

		if (options.dateTo) {
			query = query.lte('start_date', options.dateTo.toISOString());
		}

		// Determine the sort order
		if (options.justAnnounced === true) {
			query = query.order('announcement_date', { ascending: false });
		} else {
			query = query.order('start_date', { ascending: true });
		}

		// Limit the amount of events to return
		if (typeof options.range !== 'undefined') {
			query = query.range(options.range.from, options.range.to);
		} else {
			// When no range is provided, use the first 100 events
			query = query.range(0, 99);
		}

		const { count, data, error } = await query;

		if (data) {
			return {
				events: data,
				count,
			};
		}

		handleError(error.message);
	} catch (error) {
		handleError(error);
	}

	return {
		events: [],
		count: null,
	};
}
